// Shared formatting conventions for the web app — the single source of
// truth for how numeric values render in the UI. ANYTHING that puts a
// number on screen should route through this module so the visual
// language stays uniform as new formatters get added.
//
// === Bloomberg-style conventions ===
//
//   1. Compact magnitudes use UPPERCASE suffixes: K, M, B, T.
//      ≥ 1e3 → K, ≥ 1e6 → M, ≥ 1e9 → B, ≥ 1e12 → T.
//
//   2. Money renders with 2 decimal places by default — "£1.50M",
//      "£500.00", "(£250.00K)". Sub-thousand uses fixed 2dp so columns
//      align vertically.
//
//   3. Counts render with 1 decimal place by default — "1.5M rows",
//      "847" (sub-thousand drops the decimal, integers with thousands
//      grouping).
//
//   4. Percent renders with 2 decimal places by default — "12.34%".
//      Pass `signed: true` for delta indicators ("+12.34%", "-3.50%")
//      which keep the explicit +/- sign instead of accounting brackets.
//
//   5. Negatives wrap in parentheses, never prefixed with a minus —
//      "(£5.23M)", "(1.5K)", "(12.34%)". This is the accounting
//      convention; delta indicators are the only exception.
//
//   6. Empty / missing values render as EMPTY_VALUE ("—"). Never a
//      blank cell, never "null", never "0" as a stand-in.
//
//   7. Currency symbol sits OUTSIDE the bracket pair — "(£5.23M)",
//      not "£(5.23M)". Matches accounting statements.
//
//   8. en-GB locale for thousand-grouping in sub-thousand integer
//      values.

/** The display string for any null / undefined / NaN numeric value.
 *  Don't render a blank cell — empty data must be visible as such. */
export const EMPTY_VALUE = '—';

// === Primitives =====================================================

/** Wrap the rendered body in parentheses when the numeric value is
 *  negative. Pass the already-formatted absolute-value body and the
 *  original numeric value; this checks the sign. Zero is never wrapped. */
export function wrapNegative(value: number, body: string): string {
  return value < 0 ? `(${body})` : body;
}

interface CompactOpts {
  /** Decimal places for the scaled compact value (e.g. the 50 in 1.50M). */
  decimals: number;
  /** How to render values below 1,000.
   *   - 'integer'  thousands-grouped integer (no decimals): "847"
   *   - 'fixed'    fixed decimals matching the compact precision: "847.00" */
  subThousand: 'integer' | 'fixed';
}

/** Bloomberg-style compact suffix on the absolute value. Returns the
 *  body string WITHOUT a sign or currency — callers compose the final
 *  output with wrapNegative and a currency prefix as needed.
 *
 *    compactBody(1500, {decimals: 2, subThousand: 'integer'})  // "1.50K"
 *    compactBody(847,  {decimals: 2, subThousand: 'fixed'})    // "847.00"
 *    compactBody(847,  {decimals: 1, subThousand: 'integer'})  // "847"
 *    compactBody(1.5e9, {decimals: 2, subThousand: 'integer'}) // "1.50B" */
export function compactBody(value: number, opts: CompactOpts): string {
  const abs = Math.abs(value);
  if (abs >= 1e12) return `${(abs / 1e12).toFixed(opts.decimals)}T`;
  if (abs >= 1e9) return `${(abs / 1e9).toFixed(opts.decimals)}B`;
  if (abs >= 1e6) return `${(abs / 1e6).toFixed(opts.decimals)}M`;
  if (abs >= 1e3) return `${(abs / 1e3).toFixed(opts.decimals)}K`;
  if (opts.subThousand === 'fixed') {
    return abs.toLocaleString('en-GB', {
      minimumFractionDigits: opts.decimals,
      maximumFractionDigits: opts.decimals,
    });
  }
  return abs.toLocaleString('en-GB');
}

// === High-level formatters =========================================

export interface MoneyOpts {
  /** Currency symbol to prefix the body. Pass "£", "€", "$", "kr" —
   *  not an ISO currency code. Defaults to no prefix. */
  currency?: string;
  /** Decimal places. Defaults to 2 (Bloomberg standard for money). */
  decimals?: number;
}

/** Format a numeric value as money — Bloomberg-style compact uppercase
 *  suffix, 2 decimal places, accounting brackets for negatives.
 *    formatMoney(1_500_000, {currency: '£'})   // "£1.50M"
 *    formatMoney(-250.50, {currency: '€'})     // "(€250.50)"
 *    formatMoney(null)                          // "—" */
export function formatMoney(value: number | null | undefined, opts: MoneyOpts = {}): string {
  if (value == null || !Number.isFinite(value)) return EMPTY_VALUE;
  const { currency = '', decimals = 2 } = opts;
  const body = `${currency}${compactBody(value, { decimals, subThousand: 'fixed' })}`;
  return wrapNegative(value, body);
}

/** Like formatMoney, but trims trailing fractional zeros so whole values read
 *  clean (€150K, €100M) while fractional ones keep precision (€1.05M, €1.5M).
 *  Used by the filter sliders. */
export function formatMoneyTrim(value: number | null | undefined, opts: MoneyOpts = {}): string {
  if (value == null || !Number.isFinite(value)) return EMPTY_VALUE;
  const { currency = '', decimals = 2 } = opts;
  const body = compactBody(value, { decimals, subThousand: 'integer' }).replace(
    /\.?0+([KMBT])$/,
    '$1',
  );
  return wrapNegative(value, `${currency}${body}`);
}

export interface CountOpts {
  /** Decimal places on the compact value. Defaults to 1. */
  decimals?: number;
}

/** Format an integer count — compact suffix at thousands and above,
 *  thousands-grouped integer below 1,000. Accounting brackets for
 *  negatives.
 *    formatCount(1_500_000) // "1.5M"
 *    formatCount(847)       // "847"
 *    formatCount(-1234)     // "(1.2K)"
 *    formatCount(null)      // "—" */
export function formatCount(value: number | null | undefined, opts: CountOpts = {}): string {
  if (value == null || !Number.isFinite(value)) return EMPTY_VALUE;
  const { decimals = 1 } = opts;
  return wrapNegative(value, compactBody(value, { decimals, subThousand: 'integer' }));
}

export interface PercentOpts {
  /** Decimal places. Defaults to 2 (Bloomberg standard). */
  decimals?: number;
  /** When true, always render an explicit +/- sign instead of using
   *  accounting brackets for negatives. Use for delta indicators
   *  ("+12.34%", "-3.50%") where direction is the point of the value. */
  signed?: boolean;
}

/** Format a percentage value (input is already in percent units — pass
 *  12.34 for "12.34%", not 0.1234).
 *    formatPercent(12.34)                  // "12.34%"
 *    formatPercent(-3.5)                   // "(3.50%)"
 *    formatPercent(12.34, {signed: true})  // "+12.34%"
 *    formatPercent(-3.5, {signed: true})   // "-3.50%"
 *    formatPercent(null)                   // "—" */
export function formatPercent(value: number | null | undefined, opts: PercentOpts = {}): string {
  if (value == null || !Number.isFinite(value)) return EMPTY_VALUE;
  const { decimals = 2, signed = false } = opts;
  const abs = Math.abs(value);
  const body = `${abs.toFixed(decimals)}%`;
  if (signed) {
    const sign = value > 0 ? '+' : value < 0 ? '-' : '';
    return `${sign}${body}`;
  }
  return wrapNegative(value, body);
}

/** Plain integer with thousands grouping (no compact). Use for full
 *  row counts in headers, status bars, etc.
 *    formatSignedInt(174679)  // "174,679"
 *    formatSignedInt(-5000)   // "(5,000)" */
export function formatSignedInt(n: number): string {
  return wrapNegative(n, Math.abs(n).toLocaleString('en-GB'));
}
